import {add} from 'date-fns';
import {Form, DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';
import {match} from 'ts-pattern';
import {object} from 'yup';

import {useCallback, useEffect} from 'react';
import {useDispatch} from 'react-redux';

import {always, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {documentContextApi} from '@dms/api/core';
import {useGetCustomerPaymentQuery} from '@dms/api/finance';
import {
  AftersalesPaymentMethod,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentByIdQuery,
  useGetCheckoutPaymentIssueRestrictionQuery,
  usePatchCheckoutPaymentMutation,
  usePostCheckoutPaymentIssueMutation,
} from '@dms/api/sales';
import {
  DEFAULT_FOREIGN_CURRENCY,
  handleApiError,
  printPDF,
  useBank,
  useCheckoutIssuing,
  useTenant,
} from '@dms/shared';

import {
  getApiDateString,
  parseDate,
  precisionCalculation,
  RequiredTestIdProps,
  suffixTestId,
  useDebouncedCallback,
  yupDate,
  yupNumber,
  yupString,
} from 'shared/web';

import {CheckoutPaymentFormType} from '../types/CheckoutPaymentFormType';
import {IssueRestrictionResponse} from '../types/IssueRestrictionResponse';
import {CheckoutPaymentFormBody} from './CheckoutPaymentFormBody';
import {PaymentOverview} from './PaymentOverview';

interface CheckoutPaymentFormProps extends RequiredTestIdProps {
  paymentId: string;
  checkoutId: string;
  customerId: string;
  onAddAnotherDepositClick?: () => Promise<unknown>;
  onRemoveDepositClick?: () => Promise<unknown>;
  onRestrictionsChange?: (restrictions: IssueRestrictionResponse[]) => void;
  isSubmitButtonDisabled?: boolean;
  defaultPaymentType?: AftersalesPaymentMethod;
  isForeignCurrencyAllowed?: boolean;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
  serviceCaseId?: string;
  serviceOrderId?: string;
}

export function CheckoutPaymentForm(props: CheckoutPaymentFormProps) {
  const dispatch = useDispatch();
  const tenant = useTenant();
  const onRestrictionsChange = props.onRestrictionsChange;

  const {getBankAccountByNumber, primaryBankAccountOption} = useBank();

  const {
    data: customerPayment,
    isFetching: isCustomerPaymentFetching,
    isError: isCustomerPaymentError,
  } = useGetCustomerPaymentQuery({customerId: props.customerId});

  const {
    data: paymentData,
    isLoading: isPaymentLoading,
    isError: isPaymentError,
  } = useGetCheckoutPaymentByIdQuery({
    checkoutId: props.checkoutId,
    paymentId: props.paymentId,
  });

  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});

  const isConceptPayment = paymentData?.paymentState === 'CONCEPT';
  const shouldSkipIssueRestrictionsQuery = isNotNil(paymentData) && !isConceptPayment;

  const {
    data: issueRestrictions,
    isLoading: isIssueRestrictionsLoading,
    isError: isIssueRestrictionsError,
  } = useGetCheckoutPaymentIssueRestrictionQuery(
    {
      checkoutId: props.checkoutId,
      paymentId: props.paymentId,
    },
    {refetchOnMountOrArgChange: true, skip: shouldSkipIssueRestrictionsQuery}
  );

  useEffect(() => {
    if (!isConceptPayment) {
      onRestrictionsChange?.([]);
      return;
    }

    if (issueRestrictions) {
      onRestrictionsChange?.(issueRestrictions);
    } else {
      onRestrictionsChange?.([]);
    }
  }, [isConceptPayment, issueRestrictions, onRestrictionsChange]);

  const [patchCheckoutPayment] = usePatchCheckoutPaymentMutation();
  const [postCheckoutPaymentIssue, {isLoading: isPostCheckoutPaymentIssueLoading}] =
    usePostCheckoutPaymentIssueMutation();
  const {setIsCheckoutIssuing} = useCheckoutIssuing();

  const invalidateDocumentsCount = () => {
    // We need to wait for the document count to be updated through kafka between metada and php
    setTimeout(() => {
      dispatch(documentContextApi.util.invalidateTags(['documentsCount']));
    }, 1000);
  };

  const handleSaveChanges = useCallback(
    (data: CheckoutPaymentFormType) => {
      const bankAccount = getBankAccountByNumber(data.bankAccount);

      const foreignCurrencyAmount = parseFloat(
        precisionCalculation.divide(data.amount ?? 0, data.exchangeRate ?? 1).toFixed(2)
      );

      if (isNil(data.dateOfTaxableSupply) || isNil(data.dueDate) || isNil(data.issueDate)) {
        return;
      }

      return patchCheckoutPayment({
        checkoutId: props.checkoutId,
        paymentId: props.paymentId,
        body: {
          paymentAmount: {
            amount: data.amount,
            currency:
              paymentData?.paymentAmount?.currency ??
              checkoutData?.currency ??
              tenant.tenantCurrency,
          },
          foreignCurrencyPaymentAmount: checkoutData?.isForeignCurrencyPayment
            ? {
                amount: foreignCurrencyAmount,
                currency:
                  paymentData?.foreignCurrencyPaymentAmount?.currency ?? DEFAULT_FOREIGN_CURRENCY,
              }
            : undefined,
          dateOfTaxableSupply: getApiDateString(data.dateOfTaxableSupply),
          dueDate: getApiDateString(data.dueDate),
          issueDate: getApiDateString(data.issueDate),
          method: data.method,
          customerServiceContractId: data.customerServiceContractId,
          templateId: data.templateId,
          bankAccount: bankAccount
            ? {
                currency: bankAccount.currency ?? checkoutData?.currency ?? tenant.tenantCurrency,
                isPrimary: !!bankAccount.isPrimary,
                bankName: bankAccount.bankName,
                iban: bankAccount.iban,
                name: bankAccount.accountName,
                number: bankAccount.accountNumber,
                swift: bankAccount.swift,
              }
            : undefined,
          cashRegisterId: data.cashRegisterId,
          due: data.due,
          exchangeRate: data.exchangeRate ? String(data.exchangeRate) : undefined,
          note: data.note,
        },
      })
        .unwrap()
        .catch(handleApiError);
    },
    [
      checkoutData?.currency,
      checkoutData?.isForeignCurrencyPayment,
      getBankAccountByNumber,
      patchCheckoutPayment,
      paymentData?.foreignCurrencyPaymentAmount?.currency,
      paymentData?.paymentAmount?.currency,
      props.checkoutId,
      props.paymentId,
      tenant.tenantCurrency,
    ]
  );

  const debouncedSaveChanges = useDebouncedCallback(handleSaveChanges, 1000);

  const handleIssuePayment = async (data: CheckoutPaymentFormType) => {
    setIsCheckoutIssuing(true);

    await handleSaveChanges(data);

    await postCheckoutPaymentIssue({
      checkoutId: props.checkoutId,
      paymentId: props.paymentId,
      body: {issuedOn: getApiDateString(data.issueDate)},
    })
      .unwrap()
      .then((res) => {
        if (res?.file?.pdfUrl) {
          printPDF(res.file.pdfUrl);
        }
      })
      .then(invalidateDocumentsCount)
      .catch(handleApiError)
      .finally(() => {
        setIsCheckoutIssuing(false);
      });
  };

  // Fallback when bank payment is set as default value but not allowed by customer payment settings
  const defaultPaymentMethod = match<
    [AftersalesPaymentMethod | undefined, boolean],
    AftersalesPaymentMethod | undefined
  >([paymentData?.method ?? props?.defaultPaymentType, !!customerPayment?.allowBankTransfer])
    .with(['BANK_TRANSFER', false], always('CARD'))
    .otherwise(([paymentType]) => paymentType);

  const isLoading =
    isPaymentLoading ||
    isCustomerPaymentFetching ||
    isCheckoutLoading ||
    isIssueRestrictionsLoading;
  const isError =
    isPaymentError || isCustomerPaymentError || isCheckoutError || isIssueRestrictionsError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      <Show when={paymentData?.paymentState === 'CONCEPT'}>
        <Form<CheckoutPaymentFormType>
          mode="onSubmit"
          defaultValues={{
            amount: paymentData?.paymentAmount.amount ?? undefined,
            bankAccount:
              paymentData?.bankAccount?.id ?? primaryBankAccountOption?.value ?? undefined,
            note: paymentData?.note ?? undefined,
            method: defaultPaymentMethod,
            due: customerPayment?.invoiceMaturity ?? paymentData?.due ?? 0,
            dueDate: isNotNil(customerPayment?.invoiceMaturity)
              ? add(new Date(), {days: customerPayment?.invoiceMaturity})
              : parseDate(paymentData?.dueDate ?? new Date().toISOString()),
            issueDate: parseDate(paymentData?.issuedOn ?? new Date().toISOString()),
            dateOfTaxableSupply: parseDate(
              paymentData?.dateOfTaxableSupply ?? new Date().toISOString()
            ),
            currency: paymentData?.foreignCurrencyPaymentAmount?.currency,
            customerServiceContractId: paymentData?.customerServiceContractId ?? undefined,
            templateId: paymentData?.templateId ?? undefined,
            exchangeRate: paymentData?.exchangeRate
              ? parseFloat(paymentData.exchangeRate)
              : undefined,
          }}
          schema={formSchema}
          onChange={debouncedSaveChanges}
          onSubmit={handleIssuePayment}
          isDisabled={props.isEditingDisabled}
        >
          {(control, formApi) => (
            <CheckoutPaymentFormBody
              control={control}
              customerId={props.customerId}
              formApi={formApi}
              checkoutId={props.checkoutId}
              allowedPaymentMethods={paymentData?.availablePaymentMethods}
              isSubmitButtonDisabled={props.isSubmitButtonDisabled}
              issueRestrictions={isConceptPayment ? issueRestrictions : []}
              onSaveChanges={debouncedSaveChanges}
              payment={paymentData!}
              onAddAnotherDepositClick={props.onAddAnotherDepositClick}
              onRemoveDepositClick={props.onRemoveDepositClick}
              isForeignCurrencyAllowed={props.isForeignCurrencyAllowed}
              totalAmount={checkoutData?.totalPrice?.withVat?.amount ?? 0}
              isEditingDisabled={props.isEditingDisabled}
              isCompletingDisabled={props.isCompletingDisabled}
              isPostCheckoutPaymentIssueLoading={isPostCheckoutPaymentIssueLoading}
              data-testid={suffixTestId('body', props)}
            />
          )}
        </Form>
      </Show>
      <Show when={paymentData?.paymentState === 'PENDING' || paymentData?.paymentState === 'PAID'}>
        {paymentData && (
          <PaymentOverview
            payment={paymentData}
            checkoutId={props.checkoutId}
            onAddAnotherDepositClick={props.onAddAnotherDepositClick}
            isEditingDisabled={props.isEditingDisabled}
            isCompletingDisabled={props.isCompletingDisabled}
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            data-testid={suffixTestId('overview', props)}
          />
        )}
      </Show>
    </DataStatus>
  );
}

const formSchema = object({
  amount: yupNumber.required(),
  method: yupString.required(),
  issueDate: yupDate.required(),
  bankAccount: yupString.when('method', {
    is: 'BANK_TRANSFER',
    then: yupString.required().nullable(),
    otherwise: yupString.optional().nullable(),
  }),
  cashRegisterId: yupString.when('method', {
    is: 'CASH',
    then: yupString.required().nullable(),
    otherwise: yupString.optional().nullable(),
  }),
  due: yupNumber.required().min(0).max(3650),
  dueDate: yupDate.required(),
  dateOfTaxableSupply: yupDate.required(),
  note: yupString.optional(),
  exchangeRate: yupNumber.when('currency', {
    is: isNotNil,
    then: yupNumber.required().min(0),
    otherwise: yupNumber,
  }),
  currency: yupString,
});
